/**
 * The monetization rung a funnel event belongs to.
 *
 * `user_type` / `auth_state` only ever said anonymous vs authenticated, which
 * is two of the four rungs the product actually sells against. This resolves
 * the real ladder — anonymous -> email_unlocked -> registered_free -> premium —
 * so step loss can be read per tier.
 *
 * The tier RULES are not reimplemented here:
 *   - premium comes from AuthContext's `isPremium`, which is
 *     lib/auth/subscription.ts `resolveIsPremium` (paid OR unexpired promo).
 *   - the email rung is defined by EMAIL_UNLOCK_COOKIE
 *     (lib/email-unlock-cookie.ts). That cookie is httpOnly AND HMAC-signed on
 *     purpose, so a browser cannot read it. The client therefore learns the rung
 *     from the two places the server already tells it: the analyze route's 429
 *     payload (`tier: 'email'`) and the anonymous ceiling on a success payload,
 *     compared against the SAME ladder constants the server enforces
 *     (lib/auth/feature-flags.ts). Nothing about the rule is duplicated —
 *     only the observation is.
 */

import {
  ANONYMOUS_WEB_ANALYSIS_LIMITS,
  EMAIL_TIER_ANALYSIS_LIMITS,
} from '@/lib/auth/feature-flags'
import type { AnalysisFunnelMode } from '@/lib/analysis-funnel-envelope'
import { EMAIL_UNLOCK_MAX_AGE } from '@/lib/email-unlock-ttl'

export type AnalyticsUserTier =
  | 'anonymous'
  | 'email_unlocked'
  | 'registered_free'
  | 'premium'
  /** Auth identity or paid entitlement is still loading. Emitted rather than
   *  guessed: a premium visitor read as `registered_free` for the first second
   *  of every session is a wrong answer, not a missing one. */
  | 'unknown'

const EMAIL_UNLOCK_HINT_KEY = 'wgit_email_unlock_hint'

/**
 * The hint may never outlive the cookie it infers. EMAIL_UNLOCK_MAX_AGE is the
 * real Set-Cookie Max-Age (lib/email-unlock-ttl.ts), so the stored expiry is
 * the earliest moment the cookie could be gone — a browser holding an expired
 * hint drops to `anonymous`, which is the honest answer for a rung we can no
 * longer observe.
 */
const EMAIL_UNLOCK_HINT_TTL_MS = EMAIL_UNLOCK_MAX_AGE * 1000

/**
 * `weed` shares the diagnose counter by design (see the featureForMode note in
 * lib/auth/usage-tracking.ts), so it reads the diagnose rung of both ladders.
 */
function ladderKey(mode: AnalysisFunnelMode): 'identify' | 'diagnose' {
  return mode === 'identify' ? 'identify' : 'diagnose'
}

/**
 * True when an anonymous ceiling reported by the analyze route can only have
 * come from the email tier. Reads the shipped constants rather than a copy, so
 * it cannot drift when the ladder is retuned; returns false when the two rungs
 * are equal (no observable difference to infer from).
 */
export function anonymousLimitImpliesEmailTier(
  mode: AnalysisFunnelMode,
  limit: number | null | undefined,
): boolean {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) return false
  const key = ladderKey(mode)
  const anon = ANONYMOUS_WEB_ANALYSIS_LIMITS[key]
  const email = EMAIL_TIER_ANALYSIS_LIMITS[key]
  if (email <= anon) return false
  return limit >= email
}

/**
 * Persist the observation so the rung survives a reload inside the flow. Stored
 * as the absolute epoch-ms the hint stops being believable, never as a bare
 * marker: the cookie expires and the next visitor on this browser is a
 * different person.
 */
export function markEmailUnlockHint(now: number = Date.now()): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      EMAIL_UNLOCK_HINT_KEY,
      String(now + EMAIL_UNLOCK_HINT_TTL_MS),
    )
  } catch {
    /* storage disabled — the tier just falls back to 'anonymous' */
  }
}

/**
 * Anything that is not an unexpired timestamp reads as no hint, and is cleared.
 * That includes the legacy `'1'` marker written before the hint had an expiry:
 * it carries no evidence of freshness, so it cannot be trusted, and the next
 * analyze response re-stamps it for a visitor who really does hold the cookie.
 */
export function hasEmailUnlockHint(now: number = Date.now()): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = window.localStorage.getItem(EMAIL_UNLOCK_HINT_KEY)
    if (!raw) return false
    const expiresAt = Number(raw)
    if (Number.isFinite(expiresAt) && expiresAt > now) return true
    window.localStorage.removeItem(EMAIL_UNLOCK_HINT_KEY)
    return false
  } catch {
    return false
  }
}

export function clearEmailUnlockHint(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(EMAIL_UNLOCK_HINT_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * Resolve the rung. `emailUnlocked` is only consulted for signed-out visitors —
 * an account always outranks an email, and premium always outranks an account.
 *
 * `tierKnown: false` short-circuits to `unknown`. Callers pass it while auth
 * identity, or (for a signed-in visitor) the subscription lookup, is still in
 * flight; `isPremium` is `false` in that window for a paying visitor too, so
 * resolving it would emit `registered_free` on every scan started before the
 * entitlement lands. Signed-out visitors never enter that window — their rung
 * needs no subscription answer — so the anonymous rung is not delayed by it.
 */
export function resolveAnalyticsUserTier(input: {
  isAuthenticated: boolean
  isPremium?: boolean | null
  emailUnlocked?: boolean | null
  tierKnown?: boolean
}): AnalyticsUserTier {
  if (input.tierKnown === false) return 'unknown'
  if (input.isPremium === true) return 'premium'
  if (input.isAuthenticated) return 'registered_free'
  if (input.emailUnlocked ?? hasEmailUnlockHint()) return 'email_unlocked'
  return 'anonymous'
}
